import { memo, useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { PltComputedFields, PltInputFields } from '../../../types/pointLoadTest';
import { FieldEvidenceCellButton } from '../../../components/evidence/FieldEvidenceCellButton';
import { isValidValidity } from './computations';
import { useTestFormulas } from '../../../hooks/useTestFormulas';
import { FormulaHeaderTooltip } from '../components/FormulaHeaderTooltip';
import {
  PLT_DIRECTION_OPTIONS,
  PLT_INPUT_NUM_COLUMNS,
  PLT_RESULT_COLUMNS,
  PLT_TEST_TYPE_OPTIONS,
  PLT_VALIDITY_SELECT_OPTIONS,
  type PltNumColumn,
} from './pointLoadFields';

export interface PltGridRow {
  workOrderDetailKey: string;
  sampleId: string | null;
  borehole: string | null;
  depthTop: number | null;
  depthBase: number | null;
  input: PltInputFields;
  computed: PltComputedFields;
}

type RowChange = (workOrderDetailKey: string, field: keyof PltInputFields, value: string | number | null) => void;

interface PLTBatchGridProps {
  rows: PltGridRow[];
  onChange: RowChange;
  readOnly?: boolean;
  /** WOD key (lowercase) → row already has a Gauge PSI evidence photo. */
  evidenceHas?: Record<string, boolean>;
  onEvidenceChange?: (workOrderDetailKey: string, hasEvidence: boolean) => void;
}

/**
 * Fixed-width-friendly number format. Columns are preset (table-layout: fixed),
 * and this keeps a single value from blowing past a cell: extreme magnitudes
 * collapse to compact exponential (e.g. 1.27e+20) so typing more digits never
 * resizes the table.
 */
const fmt = (v: number | null | undefined, d: number): string => {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  const a = Math.abs(n);
  if (a !== 0 && (a >= 1e6 || a < 1e-4)) return n.toExponential(2);
  return n.toFixed(d);
};

const depthText = (top: number | null, base: number | null): string => {
  if (top === null || top === undefined) return '—';
  if (base !== null && base !== undefined && base > top) return `${top.toFixed(2)} to ${base.toFixed(2)}`;
  return top.toFixed(2);
};

/** Per-row calculation status, mirroring the mockup's Status column. */
const rowStatus = (row: PltGridRow): 'Calculated' | 'Missing load' | 'Incomplete' => {
  if (row.computed.is50Mpa !== null && row.computed.is50Mpa !== undefined) return 'Calculated';
  if (row.input.gaugePsi === null || row.input.gaugePsi === undefined) return 'Missing load';
  return 'Incomplete';
};

// Preset column widths (px), left → right, so the table never reflows.
const LEAD_W = [38, 104, 84, 96, 122, 140, 152]; // #, Sample ID, Borehole, Depth, Test Type, Direction, Validity
/** Unit / rock type free-text column (BUG-2026-0150 — template col K "UNIT"). */
const UNIT_W = 100;
/** Gauge PSI evidence-photo camera button (sits right after the Gauge column). */
const EVID_W = 40;
const TRAIL_W = [86, 54, 152];                    // Status, Valid, Notes
const SCROLL_STEP = 360;

// ── One row — memoized so typing in a cell only re-renders THAT row, not all
//    31 (the heavy MUI inputs were re-rendering on every keystroke / filter). ──
interface PltRowViewProps {
  row: PltGridRow;
  index: number;
  onChange: RowChange;
  readOnly?: boolean;
  hasEvidence: boolean;
  onEvidenceChange?: (workOrderDetailKey: string, hasEvidence: boolean) => void;
}

const PltRowView = memo(({ row, index, onChange, readOnly, hasEvidence, onEvidenceChange }: PltRowViewProps) => {
  const theme = useTheme();
  const calcBg = alpha(theme.palette.primary.main, 0.05);
  const border = `1px solid ${theme.palette.divider}`;
  const cellSx = { border, padding: '3px 5px', textAlign: 'center' as const, whiteSpace: 'nowrap' as const };
  const clipSx = { overflow: 'hidden', textOverflow: 'ellipsis' as const };
  const inputBg = theme.palette.background.paper;
  const inputBorder = alpha(theme.border.color, 0.6);
  const emptyBg = alpha(theme.palette.warning.main, 0.09);
  const emptyBorder = alpha(theme.palette.warning.main, 0.45);

  // Native <input>/<select> — MUI TextField/Select are ~10–20× heavier to mount,
  // and 31 rows × 9 fields was the page's real bottleneck. Same look, far faster.
  const fieldBaseSx = {
    width: '100%', boxSizing: 'border-box', height: 26, borderRadius: '4px',
    backgroundColor: inputBg, border: `1px solid ${inputBorder}`, color: theme.palette.text.primary,
    fontFamily: 'inherit', fontSize: '0.78rem', outline: 'none',
    '&:focus': { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}` },
    '&:disabled': { color: theme.palette.text.disabled, backgroundColor: alpha(theme.palette.action.disabledBackground, 0.4) },
  };

  const numInput = (col: PltNumColumn) => {
    const val = row.input[col.field];
    const needsFill = !readOnly && !!col.required && (val === null || val === undefined || (val as unknown) === '');
    return (
      <Box
        component="input"
        type="number"
        value={(val as number | null) ?? ''}
        disabled={readOnly}
        autoComplete="off"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const v = e.target.value;
          onChange(row.workOrderDetailKey, col.field, v === '' ? null : Number(v));
        }}
        sx={{
          ...fieldBaseSx,
          textAlign: 'center', padding: '2px 4px',
          backgroundColor: needsFill ? emptyBg : inputBg,
          borderColor: needsFill ? emptyBorder : inputBorder,
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
          MozAppearance: 'textfield',
        }}
      />
    );
  };

  const selectCell = (field: keyof PltInputFields, options: { value: string; label: string }[]) => (
    <Box
      component="select"
      value={(row.input[field] as string) ?? ''}
      disabled={readOnly}
      onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(row.workOrderDetailKey, field, e.target.value)}
      sx={{ ...fieldBaseSx, padding: '2px 4px', cursor: readOnly ? 'default' : 'pointer' }}
    >
      {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
    </Box>
  );

  const valid = isValidValidity(row.input.validity);
  const computed = row.computed;

  return (
    <Box component="tr" sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.03) } }}>
      <Box component="td" sx={cellSx}>{index}</Box>
      <Box component="td" sx={{ ...cellSx, ...clipSx, textAlign: 'left', fontWeight: 600 }}>{row.sampleId ?? '—'}</Box>
      <Box component="td" sx={{ ...cellSx, ...clipSx }}>{row.borehole ?? '—'}</Box>
      <Box component="td" sx={{ ...cellSx, ...clipSx }}>{depthText(row.depthTop, row.depthBase)}</Box>
      <Box component="td" sx={cellSx}>{selectCell('testType', PLT_TEST_TYPE_OPTIONS)}</Box>
      <Box component="td" sx={cellSx}>{selectCell('loadDirection', PLT_DIRECTION_OPTIONS)}</Box>
      <Box component="td" sx={cellSx}>{selectCell('validity', PLT_VALIDITY_SELECT_OPTIONS)}</Box>
      {/* Unit / rock type — free text (BUG-2026-0150, template col K) */}
      <Box component="td" sx={cellSx}>
        <Box
          component="input"
          type="text"
          value={row.input.unit ?? ''}
          disabled={readOnly}
          autoComplete="off"
          placeholder="e.g. Sandstone"
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(row.workOrderDetailKey, 'unit', e.target.value || null)}
          sx={{ ...fieldBaseSx, padding: '2px 5px', fontSize: '0.76rem', textAlign: 'left' }}
        />
      </Box>
      {PLT_INPUT_NUM_COLUMNS.map((c) => (
        // The Gauge PSI evidence camera cell rides directly after the gauge input
        <Box component="td" key={c.field} sx={cellSx} colSpan={1}>{numInput(c)}</Box>
      )).flatMap((cell, i) => (i === 0 ? [cell, (
        <Box component="td" key="gaugeEvidence" sx={{ ...cellSx, padding: '2px' }}>
          <FieldEvidenceCellButton
            workOrderDetailKey={row.workOrderDetailKey}
            testTypeId="pointLoad"
            fieldKey="gaugePsi"
            presumedHas={hasEvidence}
            readOnly={readOnly}
            onEvidenceChange={onEvidenceChange}
          />
        </Box>
      )] : [cell]))}
      {PLT_RESULT_COLUMNS.map((c) => (
        <Box component="td" key={c.field} sx={{ ...cellSx, ...clipSx, backgroundColor: calcBg, fontFamily: 'ui-monospace, monospace', fontSize: '0.76rem' }}>
          {fmt((computed as unknown as Record<string, number | null>)[c.field], c.decimals)}
        </Box>
      ))}
      <Box component="td" sx={{ ...cellSx, ...clipSx, backgroundColor: calcBg, fontSize: '0.72rem', color: rowStatus(row) === 'Calculated' ? theme.palette.success.dark : theme.palette.text.secondary }}>
        {rowStatus(row)}
      </Box>
      <Box component="td" sx={cellSx}>
        <Box component="span" sx={{
          display: 'inline-block', minWidth: 30, px: 0.75, py: '1px', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 700,
          backgroundColor: valid ? alpha(theme.palette.success.main, 0.16) : alpha(theme.palette.error.main, 0.16),
          color: valid ? theme.palette.success.dark : theme.palette.error.dark,
        }}>{valid ? 'Yes' : 'No'}</Box>
      </Box>
      <Box component="td" sx={cellSx}>
        <Box
          component="input"
          type="text"
          value={row.input.notes ?? ''}
          disabled={readOnly}
          autoComplete="off"
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(row.workOrderDetailKey, 'notes', e.target.value || null)}
          sx={{ ...fieldBaseSx, padding: '2px 5px', fontSize: '0.76rem', textAlign: 'left' }}
        />
      </Box>
    </Box>
  );
});
PltRowView.displayName = 'PltRowView';

export const PLTBatchGrid = ({ rows, onChange, readOnly, evidenceHas, onEvidenceChange }: PLTBatchGridProps) => {
  const theme = useTheme();
  const { formulaFor } = useTestFormulas('pointLoad');
  const groupBg = '#F5FAF3';
  const calcBg = alpha(theme.palette.primary.main, 0.05);
  const border = `1px solid ${theme.palette.divider}`;

  const cellSx = { border, padding: '3px 5px', textAlign: 'center' as const, whiteSpace: 'nowrap' as const };
  const headCellSx = { ...cellSx, backgroundColor: '#EEF2E6', fontWeight: 700, fontSize: '0.72rem' };

  const colWidths = [
    ...LEAD_W,
    UNIT_W,
    // Evidence camera column rides directly after the first input (Gauge PSI)
    ...PLT_INPUT_NUM_COLUMNS.flatMap((c, i) => (i === 0 ? [c.width, EVID_W] : [c.width])),
    ...PLT_RESULT_COLUMNS.map((c) => c.width),
    ...TRAIL_W,
  ];
  const tableWidth = colWidths.reduce((a, b) => a + b, 0);

  // ── horizontal scroll via floating arrows (native scrollbars hidden) ───────
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState({ left: false, right: false });

  const refresh = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollWidth - el.clientWidth > 4;
    setScroll({
      left: el.scrollLeft > 4,
      right: overflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    refresh();
    const on = () => refresh();
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [refresh, rows.length, tableWidth]);

  const onMainScroll = () => refresh();
  const pageScroll = (dir: -1 | 1) => scrollRef.current?.scrollBy({ left: dir * SCROLL_STEP, behavior: 'smooth' });
  const hideScrollbar = {
    scrollbarWidth: 'none' as const,
    msOverflowStyle: 'none' as const,
    '&::-webkit-scrollbar': { display: 'none' },
  };

  const arrowSx = {
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 5,
    width: 30,
    height: 30,
    backgroundColor: alpha(theme.palette.background.paper, 0.94),
    color: theme.palette.primary.main,
    border: `1px solid ${theme.border.color}`,
    boxShadow: 2,
    '&:hover': { backgroundColor: theme.palette.background.paper },
  };

  return (
    <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', border, borderRadius: 1, overflow: 'hidden' }}>
      {/* Main scroll container — native scrollbars hidden, scroll via arrows/wheel */}
      <Box ref={scrollRef} onScroll={onMainScroll} sx={{ overflow: 'auto', flex: 1, ...hideScrollbar }}>
        <Box component="table" sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: tableWidth, minWidth: tableWidth, fontSize: '0.78rem' }}>
          <colgroup>
            {colWidths.map((w, i) => (<col key={i} style={{ width: w }} />))}
          </colgroup>
          <Box component="thead" sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
            {/* Group header row */}
            <Box component="tr">
              <Box component="th" sx={{ ...headCellSx, backgroundColor: groupBg }}>#</Box>
              <Box component="th" colSpan={3} sx={{ ...headCellSx, backgroundColor: groupBg }}>Pre-loaded Sample Info</Box>
              <Box component="th" colSpan={3} sx={{ ...headCellSx, backgroundColor: groupBg }}>Type &amp; Validity</Box>
              {/* +1 Unit / rock-type text column (BUG-2026-0150), +1 Gauge evidence camera */}
              <Box component="th" colSpan={PLT_INPUT_NUM_COLUMNS.length + 2} sx={{ ...headCellSx, backgroundColor: groupBg }}>Inputs</Box>
              <Box component="th" colSpan={PLT_RESULT_COLUMNS.length + 1} sx={{ ...headCellSx, backgroundColor: calcBg }}>Calculated Results</Box>
              <Box component="th" sx={{ ...headCellSx, backgroundColor: groupBg }}>Valid</Box>
              <Box component="th" sx={{ ...headCellSx, backgroundColor: groupBg }}>Notes</Box>
            </Box>
            {/* Column header row */}
            <Box component="tr">
              <Box component="th" sx={headCellSx}>INT</Box>
              <Box component="th" sx={headCellSx}>Sample ID</Box>
              <Box component="th" sx={headCellSx}>Borehole</Box>
              <Box component="th" sx={headCellSx}>Depth (m)</Box>
              <Box component="th" sx={headCellSx}>Test Type</Box>
              <Box component="th" sx={headCellSx}>Direction</Box>
              <Box component="th" sx={headCellSx}>Validity</Box>
              <Box component="th" sx={headCellSx}>Unit</Box>
              {PLT_INPUT_NUM_COLUMNS.map((c) => {
                const reg = formulaFor(c.field);
                return (
                  <FormulaHeaderTooltip key={c.field} label={`${c.label}${c.unit ? ` (${c.unit})` : ''}`} formula={reg?.formula} note={reg?.note}>
                    <Box component="th" sx={headCellSx}>
                      {c.label}{c.unit ? <span style={{ fontWeight: 400 }}><br />{c.unit}</span> : null}
                    </Box>
                  </FormulaHeaderTooltip>
                );
              }).flatMap((th, i) => (i === 0 ? [th, (
                <Box component="th" key="gaugeEvidenceHead" sx={headCellSx} title="Gauge PSI evidence photo">
                  📷
                </Box>
              )] : [th]))}
              {PLT_RESULT_COLUMNS.map((c) => {
                const reg = formulaFor(c.field);
                return (
                  <FormulaHeaderTooltip key={c.field} label={`${c.label}${c.unit ? ` (${c.unit})` : ''}`} formula={reg?.formula} note={reg?.note}>
                    <Box component="th" sx={{ ...headCellSx, backgroundColor: calcBg }}>
                      {c.label}{c.unit ? <span style={{ fontWeight: 400 }}><br />{c.unit}</span> : null}
                    </Box>
                  </FormulaHeaderTooltip>
                );
              })}
              <Box component="th" sx={{ ...headCellSx, backgroundColor: calcBg }}>Status</Box>
              <Box component="th" sx={headCellSx}>Test</Box>
              <Box component="th" sx={headCellSx}>Notes</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((row, idx) => (
              <PltRowView
                key={row.workOrderDetailKey} row={row} index={idx + 1} onChange={onChange} readOnly={readOnly}
                hasEvidence={!!evidenceHas?.[row.workOrderDetailKey.toLowerCase()]}
                onEvidenceChange={onEvidenceChange}
              />
            ))}
            {rows.length === 0 && (
              <Box component="tr">
                <Box component="td" colSpan={colWidths.length} sx={{ ...cellSx, padding: 3, color: theme.palette.text.secondary }}>
                  <Typography variant="body2">No Point Load samples found in this work order.</Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Floating side scroll arrows */}
      {scroll.left && (
        <IconButton size="small" onClick={() => pageScroll(-1)} aria-label="Scroll left" sx={{ ...arrowSx, left: 6 }}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      )}
      {scroll.right && (
        <IconButton size="small" onClick={() => pageScroll(1)} aria-label="Scroll right" sx={{ ...arrowSx, right: 6 }}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
